import { useCallback, useEffect, useRef, useState } from "react";
import { AuthMessage, openCenteredPopup } from "./authPopup";
import { takeLoginNext } from "../../utils/lastProduct";
import { useLanguage } from "../../components/ui/LanguageProvider";

/**
 * Google 팝업 로그인 훅.
 * - login(): google-url 요청 → 팝업 오픈(차단 시 전체 리다이렉트 폴백)
 * - 팝업(AuthCallbackPage)이 보내는 결과를 수신해 성공 시 /home, 실패 시 에러 표시
 */
export function useGoogleLogin() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);
  // 콜백·리스너는 언어가 바뀌어도 다시 만들지 않는다 — 문구만 최신 언어로 고른다.
  const { t } = useLanguage();
  const tRef = useRef(t);
  tRef.current = t;

  const stopPopupWatch = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    function onMessage(e: MessageEvent<AuthMessage>) {
      if (e.origin !== window.location.origin) return; // 같은 출처만 신뢰
      stopPopupWatch();
      if (e.data?.type === "auth-success") {
        // SPA navigate는 AuthContext를 재검증하지 않아 방금 발급된 세션 쿠키를 못 읽는다
        // (로그인 직후 "로그인이 필요합니다" → 수동 새로고침해야 진입). 하드 리로드로 재마운트+재verify.
        window.location.assign(takeLoginNext());
      } else if (e.data?.type === "auth-error") {
        setError(e.data.message || tRef.current("로그인에 실패했습니다.", "Sign-in failed."));
        setLoading(false);
      }
    }
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      stopPopupWatch(); // 언마운트 시 폴링 정리
    };
  }, [stopPopupWatch]);

  const login = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const invite = new URLSearchParams(window.location.search).get("invite");
      const authUrl = invite
        ? `/api/auth/google-url?invite=${encodeURIComponent(invite)}`
        : "/api/auth/google-url";
      const res = await fetch(authUrl);
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error || tRef.current("로그인 URL을 가져오지 못했습니다.", "Could not get the sign-in URL."));

      const popup = openCenteredPopup(data.url, "google-login", 500, 650);
      if (!popup) {
        window.location.href = data.url; // 팝업 차단 → 전체 리다이렉트 폴백
        return;
      }

      // 사용자가 팝업을 그냥 닫으면 로딩 해제
      stopPopupWatch();
      timerRef.current = window.setInterval(() => {
        if (popup.closed) {
          stopPopupWatch();
          setLoading(false);
        }
      }, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : tRef.current("오류가 발생했습니다.", "An error occurred."));
      setLoading(false);
    }
  }, [stopPopupWatch]);

  return { login, loading, error };
}
